/**
 * Every error code in GLADIS, in `DOMAIN_CATEGORY_SPECIFIC` format.
 *
 * Centralised on purpose, while activity event names deliberately are not. The two look similar
 * and are not: activity event names are consumed by the audit read model, so a shared union would
 * couple all six contexts through one type that changes whenever any context adds an event. Error
 * codes are consumed by the HTTP error mapper and the API client, both of which already know about
 * every context by construction — and centralising buys a single exhaustive `switch` in the mapper,
 * so adding a code without mapping it is a compile error.
 *
 * See docs/architecture/coding.md §7.
 */
export enum ErrorCode {
  // --- identity -----------------------------------------------------------
  IDENT_SESSION_EXPIRED = "IDENT_SESSION_EXPIRED",
  IDENT_SESSION_INVALID = "IDENT_SESSION_INVALID",
  IDENT_LINK_INVALID = "IDENT_LINK_INVALID",
  IDENT_LINK_EXPIRED = "IDENT_LINK_EXPIRED",
  IDENT_INVITE_INVALID = "IDENT_INVITE_INVALID",
  IDENT_INVITE_EXPIRED = "IDENT_INVITE_EXPIRED",
  IDENT_INVITE_ALREADY_MEMBER = "IDENT_INVITE_ALREADY_MEMBER",
  IDENT_INVITE_SELF = "IDENT_INVITE_SELF",
  IDENT_HOUSEHOLD_NOT_FOUND = "IDENT_HOUSEHOLD_NOT_FOUND",
  IDENT_MEMBERSHIP_NOT_FOUND = "IDENT_MEMBERSHIP_NOT_FOUND",
  IDENT_USER_NOT_FOUND = "IDENT_USER_NOT_FOUND",
  IDENT_ROLE_INSUFFICIENT = "IDENT_ROLE_INSUFFICIENT",
  IDENT_OWNER_CANNOT_DELETE = "IDENT_OWNER_CANNOT_DELETE",
  IDENT_ALREADY_BOOTSTRAPPED = "IDENT_ALREADY_BOOTSTRAPPED",
  IDENT_NAME_INVALID = "IDENT_NAME_INVALID",
  IDENT_EMAIL_INVALID = "IDENT_EMAIL_INVALID",

  // --- identity, feature 020 ----------------------------------------------
  // Profile pictures. All three are read from the BYTES rather than from anything the request
  // declares about them, so a body claiming `image/webp` over a PNG is refused by the second.
  IDENT_AVATAR_TOO_LARGE = "IDENT_AVATAR_TOO_LARGE",
  IDENT_AVATAR_FORMAT_INVALID = "IDENT_AVATAR_FORMAT_INVALID",
  IDENT_AVATAR_DIMENSIONS_INVALID = "IDENT_AVATAR_DIMENSIONS_INVALID",

  // --- identity, feature 005 ----------------------------------------------
  // Membership mutations. Each says WHY the refusal happened, because "you can't" without a next
  // step is an obstacle rather than a rule — the same reasoning as IDENT_OWNER_CANNOT_DELETE.
  IDENT_OWNER_CANNOT_BE_REMOVED = "IDENT_OWNER_CANNOT_BE_REMOVED",
  IDENT_OWNER_CANNOT_LEAVE = "IDENT_OWNER_CANNOT_LEAVE",
  IDENT_CANNOT_REMOVE_SELF = "IDENT_CANNOT_REMOVE_SELF",
  IDENT_TRANSFER_TARGET_INVALID = "IDENT_TRANSFER_TARGET_INVALID",
  IDENT_NAME_CONFIRM_MISMATCH = "IDENT_NAME_CONFIRM_MISMATCH",
  /**
   * Deliberately distinct from IDENT_INVITE_EXPIRED.
   *
   * A withdrawn invite and a lapsed one are different facts, and the recipient is owed the
   * difference: one means someone changed their mind, the other means they were too slow. Collapsing
   * them is what back-dating `expires_at` would have done, and is why it was rejected.
   */
  IDENT_INVITE_REVOKED = "IDENT_INVITE_REVOKED",

  // --- savings, feature 006 -----------------------------------------------
  // Each refusal names WHY, so the interface can offer the next step rather than a dead end —
  // the same reasoning as IDENT_OWNER_CANNOT_DELETE.
  SAV_ACCOUNT_NOT_FOUND = "SAV_ACCOUNT_NOT_FOUND",
  /**
   * Deliberately distinct from SAV_ACCOUNT_NOT_FOUND.
   *
   * The account exists and the caller may see it; the refusal is about its state, and unarchiving
   * is the next step. Collapsing the two would tell someone their account had vanished.
   */
  SAV_ACCOUNT_ARCHIVED = "SAV_ACCOUNT_ARCHIVED",
  SAV_ACCOUNT_NOT_ARCHIVED = "SAV_ACCOUNT_NOT_ARCHIVED",
  SAV_ALREADY_ARCHIVED = "SAV_ALREADY_ARCHIVED",
  SAV_OWNER_REQUIRED = "SAV_OWNER_REQUIRED",
  SAV_OWNER_NOT_ALLOWED = "SAV_OWNER_NOT_ALLOWED",
  SAV_OWNER_NOT_MEMBER = "SAV_OWNER_NOT_MEMBER",
  SAV_NAME_INVALID = "SAV_NAME_INVALID",
  /**
   * The bound is the `numeric(15,2)` column's own, not an invented product ceiling.
   *
   * It exists so that no value capable of failing AT the database is ever sent to it: an expected
   * failure is returned, never thrown (Principle V).
   */
  SAV_BALANCE_OUT_OF_RANGE = "SAV_BALANCE_OUT_OF_RANGE",
  /**
   * Distinct from the future-dated WARNING, which is advisory and blocks nothing.
   *
   * This is the guard against the one mistake this module cannot undo: a snapshot can never be
   * edited or deleted, so a mis-keyed year is permanent.
   */
  SAV_DATE_OUT_OF_RANGE = "SAV_DATE_OUT_OF_RANGE",

  // --- properties, feature 008 --------------------------------------------
  // Same convention as the SAV_* block: each refusal names WHY, so the interface can offer the
  // next step rather than a dead end.
  //
  // There is deliberately NO code here for "an active property has no valuation". That is a broken
  // invariant rather than a domain refusal — nothing the household did caused it and nothing they
  // can do fixes it — so it uses SYS_INTERNAL below. coding.md §7.3 reserves 500 for SYS_*, and a
  // PROP_ code mapped to 500 would be this taxonomy quietly acquiring an exception.
  PROP_NOT_FOUND = "PROP_NOT_FOUND",
  /**
   * Deliberately distinct from PROP_NOT_FOUND.
   *
   * The property exists and the caller may see it; the refusal is about its state, and unarchiving
   * is the next step. Collapsing the two would tell someone their property had vanished.
   */
  PROP_ARCHIVED = "PROP_ARCHIVED",
  PROP_NOT_ARCHIVED = "PROP_NOT_ARCHIVED",
  PROP_ADDRESS_INVALID = "PROP_ADDRESS_INVALID",
  /** Names the permitted range, so the message is actionable rather than a relayed column check. */
  PROP_BUILD_YEAR_OUT_OF_RANGE = "PROP_BUILD_YEAR_OUT_OF_RANGE",
  /**
   * Required for NL properties only, and the rule lives in the domain rather than in a constraint.
   *
   * A conditional `check` would fire on an edit that changes the country before the label, and
   * produce a database error where the domain can produce a sentence.
   */
  PROP_ENERGY_LABEL_REQUIRED = "PROP_ENERGY_LABEL_REQUIRED",
  /**
   * The bound is the `numeric(15,2)` column's own, not an invented product ceiling.
   *
   * It exists so that no value capable of failing AT the database is ever sent to it: an expected
   * failure is returned, never thrown (Principle V).
   */
  PROP_AMOUNT_OUT_OF_RANGE = "PROP_AMOUNT_OUT_OF_RANGE",
  PROP_DATE_OUT_OF_RANGE = "PROP_DATE_OUT_OF_RANGE",
  /**
   * A property is never created without a value (FR-008).
   *
   * The form pre-fills the opening valuation from the purchase price, so this is only reachable by
   * clearing the field — or by a client that omitted it. The server supplies no default on purpose:
   * substituting the purchase price would give that client a valuation it never showed anyone.
   */
  PROP_VALUATION_REQUIRED = "PROP_VALUATION_REQUIRED",

  /**
   * The house-price index or the address service could not be used — 017.
   *
   * A 503, not a 500: the index being down is a temporary fact about the world and the member's
   * correct response is to try later. A VENDOR SHAPE CHANGE ALSO LANDS HERE — if CBS renumbers the
   * positional column this feature reads, the code stops finding values, and from the household's
   * side that is indistinguishable from an outage. Reporting it as one keeps the property page
   * rendering instead of turning a vendor's refactor into a crash.
   */
  PROP_INDEX_UNAVAILABLE = "PROP_INDEX_UNAVAILABLE",

  /**
   * This property can never be indexed — not Dutch, or its address resolves to no region.
   *
   * Distinct from PROP_INDEX_UNAVAILABLE because trying again will never help, which is the only
   * thing a member needs to know. The control is not offered for such a property, so reaching this
   * means a direct call or a race.
   */
  PROP_NOT_INDEXABLE = "PROP_NOT_INDEXABLE",

  /**
   * No member-entered valuation to index from.
   *
   * Cannot happen for a property created through the application — FR-P3c seeds one from the
   * purchase price. If it ever does, there is nothing to carry forward, and inventing a base would
   * be worse than refusing.
   */
  PROP_NO_ANCHOR = "PROP_NO_ANCHOR",

  // --- mortgages, feature 008 (same context, separate prefix) ---------------
  // A mortgage part is its own entity with its own lifecycle, so its refusals name the part rather
  // than the property. MORT_PROPERTY_ARCHIVED is the one that crosses: the part is fine, its parent
  // is not.
  MORT_NOT_FOUND = "MORT_NOT_FOUND",
  MORT_ARCHIVED = "MORT_ARCHIVED",
  MORT_NOT_ARCHIVED = "MORT_NOT_ARCHIVED",
  /**
   * Archiving or unarchiving one part while its property is archived.
   *
   * Refused so that every part archived during a property's archive window was archived BY the
   * cascade, which is what makes "restore exactly what the cascade took" unambiguous by
   * construction rather than by inspection. See specs/008-properties-mortgages/research.md R6.
   */
  MORT_PROPERTY_ARCHIVED = "MORT_PROPERTY_ARCHIVED",
  MORT_STARTS_BEFORE_PURCHASE = "MORT_STARTS_BEFORE_PURCHASE",
  MORT_RATE_OUT_OF_RANGE = "MORT_RATE_OUT_OF_RANGE",
  MORT_DURATION_OUT_OF_RANGE = "MORT_DURATION_OUT_OF_RANGE",
  MORT_PRINCIPAL_OUT_OF_RANGE = "MORT_PRINCIPAL_OUT_OF_RANGE",

  // --- expenses, feature 009 ------------------------------------------------
  // Same convention as the SAV_* and PROP_* blocks: each refusal names WHY, so the interface can
  // offer the next step rather than a dead end.
  //
  // There is deliberately NO code for "the derived mortgage-interest lines could not be produced".
  // FR-076 requires the page to render and SAY the section is unavailable rather than fail, so the
  // composition root handles that failure into a payload flag. A code here would imply an HTTP
  // status this feature's read route must never return.
  EXP_NOT_FOUND = "EXP_NOT_FOUND",
  /**
   * Deliberately distinct from EXP_NOT_FOUND.
   *
   * The expense exists and the caller may see it; the refusal is about its state, and unarchiving is
   * the next step. Collapsing the two would tell someone their expense had vanished.
   */
  EXP_ARCHIVED = "EXP_ARCHIVED",
  EXP_NOT_ARCHIVED = "EXP_NOT_ARCHIVED",
  EXP_ALREADY_ARCHIVED = "EXP_ALREADY_ARCHIVED",
  /**
   * The bound is the `numeric(15,2)` column's own, not an invented product ceiling.
   *
   * It exists so that no value capable of failing AT the database is ever sent to it: an expected
   * failure is returned, never thrown (Principle V). Zero and negative are refused here too, so the
   * member reads a sentence about the amount rather than a relayed `amount > 0` check.
   */
  EXP_AMOUNT_OUT_OF_RANGE = "EXP_AMOUNT_OUT_OF_RANGE",
  EXP_DESCRIPTION_INVALID = "EXP_DESCRIPTION_INVALID",
  EXP_CATEGORY_UNKNOWN = "EXP_CATEGORY_UNKNOWN",
  EXP_INTERVAL_UNKNOWN = "EXP_INTERVAL_UNKNOWN",
  EXP_START_DATE_INVALID = "EXP_START_DATE_INVALID",
  /**
   * Feature 024. The interval of an expense with a first payment date in force was changed without saying
   * what the new date is — a date, or an explicit null to leave it undated. Refused rather than carrying
   * the old date over, because a date stated for a yearly bill says nothing about a monthly one (FR-006).
   * 422, not 400: the request is well-formed and breaks a rule about its contents (`coding.md` §7.3).
   */
  EXP_FIRST_PAYMENT_DATE_REQUIRED = "EXP_FIRST_PAYMENT_DATE_REQUIRED",
  /**
   * Carries the actual sum, which is the whole reason it is its own code.
   *
   * "The percentages must total 100%" is not actionable; "they total 90%" is. The error object holds
   * the sum so the form can say which, without the interface re-deriving it from the shares it just
   * sent.
   */
  EXP_SPLIT_NOT_HUNDRED = "EXP_SPLIT_NOT_HUNDRED",
  /** At most two decimals, greater than zero, at most 100 — `numeric(5,2)`'s own precision. */
  EXP_SPLIT_SHARE_INVALID = "EXP_SPLIT_SHARE_INVALID",
  EXP_SPLIT_DUPLICATE_MEMBER = "EXP_SPLIT_DUPLICATE_MEMBER",
  /**
   * A share for someone who is neither a current active member nor already stored on this expense.
   *
   * The second half is the interesting one. A share belonging to a member who has since left the
   * household may be CARRIED FORWARD by a later edit but never created — which is what lets an
   * ordinary amount correction leave their percentage exactly where it was instead of silently
   * dropping it and leaving the split short of 100%. See specs/009-expenses-cards-settlement
   * research.md R10.
   */
  EXP_SPLIT_MEMBER_NOT_ALLOWED = "EXP_SPLIT_MEMBER_NOT_ALLOWED",

  // --- cards, feature 009 (same context, separate prefix) -------------------
  // A card is its own entity with its own lifecycle, so its refusals name the card rather than the
  // expense. CARD_ARCHIVED is the one that crosses: the expense is fine, the card it names is not.
  CARD_NOT_FOUND = "CARD_NOT_FOUND",
  CARD_ARCHIVED = "CARD_ARCHIVED",
  CARD_NOT_ARCHIVED = "CARD_NOT_ARCHIVED",
  CARD_ALREADY_ARCHIVED = "CARD_ALREADY_ARCHIVED",
  CARD_NAME_INVALID = "CARD_NAME_INVALID",
  /** An individual card must HAVE an owner when it is created. */
  CARD_OWNER_REQUIRED = "CARD_OWNER_REQUIRED",
  /** A joint card has none, which is what makes it joint. */
  CARD_OWNER_NOT_ALLOWED = "CARD_OWNER_NOT_ALLOWED",
  CARD_OWNER_NOT_MEMBER = "CARD_OWNER_NOT_MEMBER",
  /**
   * A malformed bank code or last-four.
   *
   * Both halves are optional and neither is a full account number — the application holds four
   * digits and a short code, because nothing here initiates a payment and the local database is
   * dumped to plaintext as part of the normal workflow.
   */
  CARD_IDENTIFIER_INVALID = "CARD_IDENTIFIER_INVALID",

  // --- income, feature 015 --------------------------------------------------
  INC_SOURCE_NOT_FOUND = "INC_SOURCE_NOT_FOUND",
  /**
   * Deliberately distinct from INC_SOURCE_NOT_FOUND, and from "ended".
   *
   * Archived means the record should never have existed and is withdrawn; ended means the income was
   * real and stopped. Only the first blocks an edit, and only the first is an error — ending is an
   * ordinary state a source spends years in.
   */
  INC_SOURCE_ARCHIVED = "INC_SOURCE_ARCHIVED",
  INC_NAME_INVALID = "INC_NAME_INVALID",
  INC_DESCRIPTION_INVALID = "INC_DESCRIPTION_INVALID",
  INC_CATEGORY_UNKNOWN = "INC_CATEGORY_UNKNOWN",
  INC_INTERVAL_UNKNOWN = "INC_INTERVAL_UNKNOWN",
  /** Zero and below are refused here: money going the other way is an expense, not negative income. */
  INC_AMOUNT_OUT_OF_RANGE = "INC_AMOUNT_OUT_OF_RANGE",
  /**
   * A malformed or implausible date. NOT a guard against backdating, which is supported and
   * warningless — income's chart reaches back before the household used GLADIS.
   */
  INC_DATE_INVALID = "INC_DATE_INVALID",
  INC_OWNER_REQUIRED = "INC_OWNER_REQUIRED",
  INC_OWNER_NOT_ALLOWED = "INC_OWNER_NOT_ALLOWED",
  INC_AMOUNT_PERIOD_NOT_FOUND = "INC_AMOUNT_PERIOD_NOT_FOUND",
  /**
   * A source always has a price.
   *
   * Refused in the domain rather than by a constraint, because the domain can name the alternative:
   * end the source if the income stopped, archive it if it was a mistake.
   */
  INC_LAST_AMOUNT_PERIOD = "INC_LAST_AMOUNT_PERIOD",
  INC_ENDED_BEFORE_START = "INC_ENDED_BEFORE_START",
  INC_ENTRY_NOT_FOUND = "INC_ENTRY_NOT_FOUND",

  // --- investments, feature 016 (roadmap slot 010) ------------------------
  INV_STOCK_NOT_FOUND = "INV_STOCK_NOT_FOUND",
  INV_TRANSACTION_NOT_FOUND = "INV_TRANSACTION_NOT_FOUND",
  INV_PRICE_NOT_FOUND = "INV_PRICE_NOT_FOUND",

  /** One row per symbol per household, compared trimmed and case-insensitively. */
  INV_SYMBOL_ALREADY_HELD = "INV_SYMBOL_ALREADY_HELD",

  /**
   * Removal is for a stock that was never used — the mistyped ticker. Once anything has been
   * traded, the transactions are the record and the stock stays. A soft-deleted transaction still
   * counts: deleting one hides a mistake, it does not unmake the history.
   */
  INV_STOCK_HAS_TRANSACTIONS = "INV_STOCK_HAS_TRANSACTIONS",

  /**
   * Not enough shares were held ON THE TRANSACTION'S OWN DATE, which is a different question from
   * whether enough are held today. Carries the date and the holding at it, so the member can act
   * without going to look.
   */
  INV_INSUFFICIENT_SHARES = "INV_INSUFFICIENT_SHARES",

  /**
   * An edit or delete that would leave a LATER sell without the shares to cover it. Distinct from
   * INV_INSUFFICIENT_SHARES because the offending row is not the one being written — the error
   * names the conflicting transaction instead.
   */
  INV_WOULD_STRAND_LATER_SELL = "INV_WOULD_STRAND_LATER_SELL",

  /**
   * A transaction or a price dated later than today. The portfolio records what has happened.
   *
   * NOTE THE ASYMMETRY WITH INCOME, WHICH HAS NO SUCH CODE AND MUST NOT. An income amount may be
   * dated forward — a raise already agreed — because it contributes nothing until the date arrives.
   * A price dated forward becomes the portfolio's CURRENT price the moment it is written, since
   * current price is whichever snapshot carries the greatest date, and it outranks every real quote
   * that follows it. Backdating stays freely allowed on both.
   */
  INV_DATE_IN_FUTURE = "INV_DATE_IN_FUTURE",
  INV_DATE_INVALID = "INV_DATE_INVALID",

  INV_SHARE_QUANTITY_INVALID = "INV_SHARE_QUANTITY_INVALID",
  INV_SYMBOL_INVALID = "INV_SYMBOL_INVALID",
  INV_NAME_INVALID = "INV_NAME_INVALID",
  INV_AMOUNT_OUT_OF_RANGE = "INV_AMOUNT_OUT_OF_RANGE",

  /** A price the system recorded. There is no member behind it to have made a mistake. */
  INV_PRICE_NOT_DELETABLE = "INV_PRICE_NOT_DELETABLE",

  // --- audit (spec 012, built as 018) -------------------------------------
  /**
   * A page cursor the client sent that this application did not issue.
   *
   * The activity feed's ONLY failure of its own, which is what a read-side context with no writes and
   * no validity rules looks like: the single thing a caller can get wrong is where they claim to be in
   * the list. 400 rather than a silent reset to page one — a feed whose cursor falls back to the
   * beginning loops over the first fifty entries and never terminates.
   */
  AUD_CURSOR_INVALID = "AUD_CURSOR_INVALID",

  // --- vehicles (spec 023) ------------------------------------------------
  VEH_NOT_FOUND = "VEH_NOT_FOUND",
  /**
   * Deliberately distinct from VEH_NOT_FOUND, on SAV_ACCOUNT_ARCHIVED's argument.
   *
   * The vehicle exists and the caller may see it; the refusal is about its state.
   */
  VEH_ARCHIVED = "VEH_ARCHIVED",
  VEH_NOT_ARCHIVED = "VEH_NOT_ARCHIVED",
  /**
   * A sold vehicle refuses every mutation, and that is NOT the same refusal as archived.
   *
   * Archiving withdraws a record that should not have existed and is reversible. A sale happened,
   * is terminal, and keeps the months the household genuinely owned the thing. A caller told
   * "archived" would go looking for a restore button that must not exist.
   */
  VEH_DISPOSED = "VEH_DISPOSED",
  VEH_NAME_INVALID = "VEH_NAME_INVALID",
  VEH_TYPE_UNKNOWN = "VEH_TYPE_UNKNOWN",
  VEH_OWNERSHIP_UNKNOWN = "VEH_OWNERSHIP_UNKNOWN",
  VEH_OWNER_NOT_MEMBER = "VEH_OWNER_NOT_MEMBER",

  VEH_VALUATION_NOT_FOUND = "VEH_VALUATION_NOT_FOUND",
  VEH_VALUATION_SOURCE_UNKNOWN = "VEH_VALUATION_SOURCE_UNKNOWN",
  /**
   * A valuation dated after today, or before the vehicle existed.
   *
   * Two codes rather than one, because the two are fixed by opposite corrections and a member told
   * only "bad date" has to guess which end they got wrong. 021's rule then carries the
   * discriminator in `details` so the sentence translates by REASON rather than by code.
   */
  VEH_VALUATION_FUTURE = "VEH_VALUATION_FUTURE",
  VEH_VALUATION_BEFORE_REGISTRATION = "VEH_VALUATION_BEFORE_REGISTRATION",
  VEH_AMOUNT_OUT_OF_RANGE = "VEH_AMOUNT_OUT_OF_RANGE",

  /**
   * A sale dated before the newest valuation the household recorded.
   *
   * Not a data-integrity rule — the database would accept it. It is refused because the sale price
   * is shown BESIDE the last valuation so the two can be compared, and a comparison against a
   * figure recorded after the sale compares nothing.
   */
  VEH_DISPOSAL_BEFORE_VALUATION = "VEH_DISPOSAL_BEFORE_VALUATION",
  VEH_DISPOSAL_BEFORE_REGISTRATION = "VEH_DISPOSAL_BEFORE_REGISTRATION",

  /**
   * 409 rather than 422, and the only 409 in this context.
   *
   * The request is well formed and the rule is about a row that already exists — the expense belongs
   * to another vehicle. That is a conflict with stored state rather than a defect in what was sent,
   * and the caller's fix is to unlink it there first.
   */
  VEH_EXPENSE_ALREADY_LINKED = "VEH_EXPENSE_ALREADY_LINKED",
  VEH_EXPENSE_NOT_LINKED = "VEH_EXPENSE_NOT_LINKED",

  // --- reserved for later contexts ----------------------------------------
  // REP_*  reporting    (spec 013)

  // --- system -------------------------------------------------------------
  SYS_INTERNAL = "SYS_INTERNAL",
  SYS_DATABASE = "SYS_DATABASE",
  SYS_UNAVAILABLE = "SYS_UNAVAILABLE",
  SYS_VALIDATION = "SYS_VALIDATION",
  SYS_CSRF = "SYS_CSRF",
}
